import React, {useCallback, useEffect, useRef, useState} from 'react';
import I18n from 'app/i18n';
import {showPopup} from 'app/utils/modals/ModalsService';
import {isDesktop, scrollToElement} from 'app/utils';
import {lootboxToChat} from 'app/utils/share_to_chat';
import SellNowSteamSkin from 'app/components/common/SellNowSteamSkin';
import SellArtifactPopup from 'app/components/my_profile/artifacts/sell_artifact_popup';
import PrizeCard from 'app/components/reusable/PrizeCard';
import {lootboxPrizeWinName} from 'app/utils/lootboxPrizeTitle';
import {lootItemMatches} from 'app/utils/lootboxPrizeMatch';
import {hideCalculatedPrice} from 'app/utils/lootboxPrizeType';
import {formatDropChance, formatLootboxMoney, lootRarity, rarityLabel} from 'app/utils/lootboxRarity';
import InventoryItemInterface from 'app/interfaces/InventoryItemInterface';
import LootInterface from 'app/interfaces/LootInterface';
import SteamSkinInterface from 'app/interfaces/SteamSkinInterface';

interface Props {
    lootboxId: number
    prize: LootInterface
    loot: LootInterface[]
    winArtifact?: InventoryItemInterface
    steamSkin?: SteamSkinInterface
    steamTrade?: SteamSkinInterface
    canSellArtifact: boolean
}

const enrichPrize = (prize: LootInterface, loot: LootInterface[]): LootInterface => {
    const matched = loot.find((item: LootInterface) => lootItemMatches(item, prize));

    if (!matched) {
        return prize;
    }

    return {
        ...prize,
        calculated_price: prize.calculated_price || matched.calculated_price,
        chance_percent: matched.chance_percent,
        rarity: matched.rarity
    };
};

const prizePrice = (prize: LootInterface, steamSkin?: SteamSkinInterface): string | null => {
    if (hideCalculatedPrice(prize)) {
        return null;
    }

    const value = Number(steamSkin?.price || prize.calculated_price || prize.money_amount || 0);

    if (value <= 0) {
        return null;
    }

    return formatLootboxMoney(value);
};

const LootboxDetailsWin = ({
    lootboxId,
    prize,
    loot,
    winArtifact,
    steamSkin,
    steamTrade,
    canSellArtifact
}: Props): React.ReactElement => {
    const enriched = enrichPrize(prize, loot);
    const rarity = lootRarity(enriched);
    const chance = formatDropChance(enriched.chance_percent);
    const price = prizePrice(enriched, steamSkin);
    const showArtifactSell = canSellArtifact && Boolean(winArtifact);
    const [steamSkinSold, setSteamSkinSold] = useState(false);
    const winRef = useRef<HTMLDivElement>(null);
    const tradeForSell = steamTrade && steamSkinSold
        ? {...steamTrade, sellable: false, sold: true}
        : steamTrade;

    useEffect(() => {
        if (!isDesktop()) {
            scrollToElement(winRef.current);
        }
    }, []);

    const handleChat = useCallback((): void => {
        lootboxToChat(lootboxId);
    }, [lootboxId]);

    const handleAfterSteamSell = useCallback((): void => {
        setSteamSkinSold(true);
    }, []);

    const handleSellArtifact = useCallback((): void => {
        if (!winArtifact) {
            return;
        }

        showPopup(
            'sell-inventory',
            (popupProps: {popupId: string}) => <SellArtifactPopup
                artifact={winArtifact}
                popupId={popupProps.popupId}
            />
        );
    }, [winArtifact]);

    return <div className="lootbox-detail__win" ref={winRef}>
        <h3 className="shop-item-detail__section-title">{I18n.t('shop.you_won')}</h3>
        <div className="lootbox-detail__win-body">
            <div className={`lootbox-detail__win-preview lootbox-detail__win-preview--${rarity}`}>
                <PrizeCard loot={enriched}/>
            </div>
            <div className="lootbox-detail__win-info">
                <span className={`lootbox-detail__win-rarity lootbox-detail__win-rarity--${rarity}`}>
                    {rarityLabel(rarity)}
                </span>
                <h2 className="lootbox-detail__win-name">{lootboxPrizeWinName(enriched)}</h2>
                {chance && <span className="lootbox-detail__win-chance">{I18n.t('shop.chance', {value: chance})}</span>}
                {price && <div className="lootbox-detail__win-price">{price}</div>}
            </div>
            <div className="lootbox-detail__win-actions">
                {steamSkin && tradeForSell && <SellNowSteamSkin
                    afterSell={handleAfterSteamSell}
                    steam_skin={steamSkin}
                    steam_trade={tradeForSell}
                    btnRemoveClassBtn
                    btnClass="shop-item-detail__btn"
                />}
                {showArtifactSell && !steamSkin && <button
                    type="button"
                    className="shop-item-detail__btn"
                    onClick={handleSellArtifact}
                >
                    {price ? I18n.t('sell_for', {amount: price}) : I18n.t('sell')}
                </button>}
                <button
                    type="button"
                    className="shop-item-detail__btn shop-item-detail__btn--ghost"
                    onClick={handleChat}
                >
                    {I18n.t('shop.send_message_to_chat')}
                </button>
            </div>
        </div>
    </div>;
};

export default LootboxDetailsWin;
